import { LogCannon } from '@ace-clublabs/log-cannon';
import { ThemeProvider } from '@ace-clublabs/shared-component-library';
import { SplitFactoryProvider } from '@splitsoftware/splitio-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { RouterProvider } from 'react-router-dom';
import ErrorPage from '@/components/shared/ErrorPage';
import LoadingContainer from '@/components/shared/LoadingContainer';
import { createRouter } from '@/features/scrollFlow/routes';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { queryClient } from '@/lib/queryClient';
import { initLogCannon } from '@/providers/log-cannon';
import useFeatureFlagsStore from '@/store/FeatureFlagsStore';
import { lightTheme } from '@/theming';
import { getSplitIOAPIKey } from '@/utils/splitIOHelper';

export const ErrorFallback = ({ resetErrorBoundary }: FallbackProps) => {
  return <ErrorPage onClickTryAgain={resetErrorBoundary} />;
};

interface FeatureFlagsLoaderProps {
  children: React.ReactNode;
  fallback: React.ReactNode;
}

const FeatureFlagsLoader: React.FC<FeatureFlagsLoaderProps> = ({
  children,
  fallback,
}) => {
  useFeatureFlags();
  const featureFlagsReady = useFeatureFlagsStore(
    (state) => state.featureFlagsReady,
  );

  if (!featureFlagsReady) {
    return fallback;
  }

  return children;
};

const router = createRouter();

export const AppProvider = () => {
  const [isLogCannonReady, setIsLogCannonReady] = useState(false);

  useEffect(() => {
    initLogCannon();
    setIsLogCannonReady(true);
  }, []);

  const splitioConfig = useMemo(() => {
    if (!isLogCannonReady) return null;

    let key: string | null = localStorage.getItem('split_key');
    if (!key) {
      key = LogCannon.getSessionId();
      if (!key) return null;
      localStorage.setItem('split_key', key);
    }

    return {
      core: {
        authorizationKey: getSplitIOAPIKey(),
        key,
      },
    };
  }, [isLogCannonReady]);

  const fallback = (
    <LoadingContainer
      message='Your travel scheduler is loading...'
      showRadios={false}
    />
  );

  if (!splitioConfig) {
    return fallback;
  }

  return (
    <React.Suspense fallback={fallback}>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <SplitFactoryProvider config={splitioConfig}>
          <FeatureFlagsLoader fallback={fallback}>
            <QueryClientProvider client={queryClient}>
              {process.env.NODE_ENV !== 'test' && <ReactQueryDevtools />}
              <ThemeProvider theme={lightTheme}>
                <RouterProvider router={router} />
              </ThemeProvider>
            </QueryClientProvider>
          </FeatureFlagsLoader>
        </SplitFactoryProvider>
      </ErrorBoundary>
    </React.Suspense>
  );
};
